import React,{useState,useEffect,useCallback,useRef} from 'react';
import {vendorAPI,productAPI,branchAPI} from '../utils/api';
import {PageHeader,SearchBar,DataTable,Badge,Modal,FormInput,fmt,fmtDate,FilterButtons} from '../components/common';
import {Plus,Edit,Trash2,Eye,FileText,Package,CheckCircle,XCircle,
  Clock,Truck,RotateCcw,ChevronDown,ChevronUp,X,RefreshCw,ShoppingCart,DollarSign} from 'lucide-react';
import toast from 'react-hot-toast';
import {useAuth} from '../context/AuthContext';
import api from '../utils/api';

// ── API helpers ──────────────────────────────────────────────────
const poAPI = {
  getAll:  p  => api.get('/purchase-orders', {params:p}),
  getById: id => api.get(`/purchase-orders/${id}`),
  create:  d  => api.post('/purchase-orders', d),
  update:  (id,d) => api.put(`/purchase-orders/${id}`, d),
  updateStatus: (id,s) => api.patch(`/purchase-orders/${id}/status`,{status:s}),
  updatePayment:(id,d) => api.patch(`/purchase-orders/${id}/payment`, d),
  delete:  id => api.delete(`/purchase-orders/${id}`),
  receive: (id,d) => api.post(`/purchase-orders/${id}/receive`, d),
};

const STATUS_COLOR={draft:'warning',sent:'info',confirmed:'primary',received:'success',partial:'accent',cancelled:'danger'};
const STATUS_LABEL={draft:'Draft',sent:'Sent',confirmed:'Confirmed',received:'Received',partial:'Partial',cancelled:'Cancelled'};
const STATUSES=Object.keys(STATUS_LABEL);
const PAY_COLOR={unpaid:'danger',partial_paid:'warning',paid:'success'};
const PAY_LABEL={unpaid:'Unpaid',partial_paid:'Part Paid',paid:'Paid'};

// ── Build UoM options from a product (base UoM + conversions) ───
function buildProductUomOpts(prod) {
  if (!prod) return [];
  const opts = [];
  // Base UoM
  if (prod.uom) {
    opts.push({
      value: String(prod.uom.id||prod.uom_id),
      label: `${prod.uom.name} (${prod.uom.abbreviation}) — Base`,
      price: parseFloat(prod.cost_price) || 0,
      factor: 1,
    });
  }
  // Conversion UoMs
  (prod.uoms || []).forEach(pu => {
    if (!pu.uom) return;
    opts.push({
      value: String(pu.uom.id || pu.uom_id),
      label: `${pu.uom.name} (${pu.uom.abbreviation}) ×${pu.conversion_factor}`,
      price: parseFloat(pu.price) || 0,
      factor: parseFloat(pu.conversion_factor) || 1,
    });
  });
  return opts;
}

// ── Line item row ────────────────────────────────────────────────
function LineRow({row,idx,products,onChange,onRemove,canSeeCost}){
  const prod = products.find(p=>p.id===parseInt(row.product_id));
  const prodUomOpts = buildProductUomOpts(prod);
  const total = (parseFloat(row.qty)||0) * (parseFloat(row.unit_cost)||0);

  const handleProductChange = e => {
    const p = products.find(x=>x.id===parseInt(e.target.value));
    // Set product, reset uom to base, prefill cost
    onChange(idx, 'product_id', e.target.value);
    if (p) {
      const baseUomId = p.uom ? String(p.uom.id || p.uom_id) : '';
      onChange(idx, 'uom_id', baseUomId);
      onChange(idx, 'unit_cost', parseFloat(p.cost_price)||0);
    } else {
      onChange(idx, 'uom_id', '');
      onChange(idx, 'unit_cost', 0);
    }
  };

  const handleUomChange = e => {
    const opt = prodUomOpts.find(o=>o.value===e.target.value);
    onChange(idx, 'uom_id', e.target.value);
    if (opt) onChange(idx, 'unit_cost', opt.price);
  };

  return(
    <tr style={{borderBottom:'1px solid var(--border)'}}>
      <td style={{padding:'6px 8px',minWidth:200}}>
        <select className="form-select" value={String(row.product_id||'')}
          onChange={handleProductChange} style={{fontSize:12}}>
          <option value="">— Select Product —</option>
          {products.map(p=><option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
        </select>
      </td>
      <td style={{padding:'6px 8px',minWidth:150}}>
        {prodUomOpts.length > 0 ? (
          <select className="form-select" value={String(row.uom_id||'')}
            onChange={handleUomChange} style={{fontSize:12}}>
            <option value="">— UoM —</option>
            {prodUomOpts.map(u=>(
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>
        ) : (
          <span style={{fontSize:11,color:'var(--td)',padding:'0 4px'}}>
            {prod ? `${prod.uom?.abbreviation||'—'}` : '—'}
          </span>
        )}
      </td>
      <td style={{padding:'6px 8px',width:90}}>
        <input type="number" min={1} className="form-input" value={row.qty||''}
          onChange={e=>onChange(idx,'qty',e.target.value)}
          style={{textAlign:'right',fontSize:13}}/>
      </td>
      <td style={{padding:'6px 8px',width:110}}>
        {canSeeCost ? (
          <input type="number" min={0} step={0.01} className="form-input" value={row.unit_cost||''}
            onChange={e=>onChange(idx,'unit_cost',e.target.value)}
            style={{textAlign:'right',fontSize:13}}/>
        ) : (
          <span style={{fontSize:11,color:'var(--td)',padding:'0 4px'}}>—</span>
        )}
      </td>
      <td style={{padding:'6px 8px',width:100,textAlign:'right',fontWeight:700,fontSize:13}}>
        {canSeeCost ? fmt(total) : '—'}
      </td>
      <td style={{padding:'6px 8px',width:36}}>
        <button type="button" className="btn btn-ghost btn-sm"
          onClick={()=>onRemove(idx)}
          style={{color:'var(--danger)',padding:4,width:28,justifyContent:'center'}}>
          <X size={13}/>
        </button>
      </td>
    </tr>
  );
}

// ── Receive Modal ────────────────────────────────────────────────
function ReceiveModal({open,po,onClose,onDone}){
  const[lines,setLines]=useState([]);
  const[saving,setSaving]=useState(false);

  useEffect(()=>{
    if(po?.items){
      setLines(po.items.map(it=>({
        id:it.id, product_id:it.product_id,
        product_name:it.product?.name||'',
        ordered:it.quantity, received_so_far:it.received_qty||0,
        receiving:it.quantity-(it.received_qty||0),
      })));
    }
  },[po]);

  if(!open||!po) return null;

  const doReceive=async()=>{
    setSaving(true);
    try{
      await poAPI.receive(po.id,{items:lines.map(l=>({id:l.id,qty:parseFloat(l.receiving)||0}))});
      toast.success('Stock received!');
      onDone();
      onClose();
    }catch(e){toast.error(e.response?.data?.error||'Failed');}
    finally{setSaving(false);}
  };

  const S={overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16}};
  return(
    <div style={S.overlay} onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:'var(--surface)',borderRadius:14,border:'1px solid var(--border)',width:'100%',maxWidth:600,maxHeight:'90vh',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'16px 20px',borderBottom:'1px solid var(--border)'}}>
          <div style={{fontWeight:700,fontSize:15,display:'flex',alignItems:'center',gap:8}}><Package size={18} color="var(--primary)"/> Receive Stock — {po.po_number}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16}/></button>
        </div>
        <div style={{overflowY:'auto',padding:20,flex:1}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{background:'var(--s2)'}}>
                {['Product','Ordered','Received','Receiving Now'].map(h=>(
                  <th key={h} style={{padding:'8px 10px',textAlign:h==='Product'?'left':'right',fontSize:11,fontWeight:700,color:'var(--tm)',borderBottom:'1px solid var(--border)'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((l,i)=>(
                <tr key={i} style={{borderBottom:'1px solid var(--border)'}}>
                  <td style={{padding:'8px 10px',fontWeight:600}}>{l.product_name}</td>
                  <td style={{padding:'8px 10px',textAlign:'right',color:'var(--td)'}}>{l.ordered}</td>
                  <td style={{padding:'8px 10px',textAlign:'right',color:'var(--success)'}}>{l.received_so_far}</td>
                  <td style={{padding:'8px 10px'}}>
                    <input type="number" min={0} max={l.ordered-l.received_so_far} className="form-input"
                      value={l.receiving}
                      onChange={e=>setLines(arr=>{const n=[...arr];n[i]={...n[i],receiving:e.target.value};return n;})}
                      style={{textAlign:'right',width:80}}/>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{padding:'14px 20px',borderTop:'1px solid var(--border)',display:'flex',gap:10,justifyContent:'flex-end'}}>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={doReceive} disabled={saving}>
            {saving?<><RefreshCw size={14} style={{animation:'spin 1s linear infinite'}}/> Saving…</>:<><CheckCircle size={14}/> Confirm Receipt</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail View Modal ────────────────────────────────────────────
function DetailModal({open,po,onClose}){
  if(!open||!po) return null;
  const total=(po.items||[]).reduce((s,i)=>s+(parseFloat(i.unit_cost)||0)*(i.quantity||0),0);
  const paid=parseFloat(po.paid_amount)||0;
  const balance=total-paid;
  return(
    <Modal open={open} onClose={onClose} title={`PO — ${po.po_number}`} width={680}>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:16}}>
        {[
          ['Vendor',po.vendor?.name||'—'],
          ['Branch',po.branch?.name||'—'],
          ['Status',<Badge key="s" variant={STATUS_COLOR[po.status]||'info'}>{STATUS_LABEL[po.status]||po.status}</Badge>],
          ['Order Date',fmtDate(po.order_date)],
          ['Expected',po.expected_date?fmtDate(po.expected_date):'—'],
          ['Payment Terms',po.payment_terms||'—'],
          ['Payment',<Badge key="ps" variant={PAY_COLOR[po.payment_status||'unpaid']}>{PAY_LABEL[po.payment_status||'unpaid']}</Badge>],
          ['Paid Amount',<span key="pa" style={{color:'var(--success)',fontWeight:700}}>{fmt(paid)}</span>],
          ['Balance Due',<span key="bal" style={{color:balance>0?'var(--danger)':'var(--success)',fontWeight:700}}>{fmt(balance)}</span>],
        ].map(([k,v])=>(
          <div key={k} style={{background:'var(--s2)',borderRadius:8,padding:'10px 12px'}}>
            <div style={{fontSize:10,color:'var(--td)',fontWeight:700,letterSpacing:.4,marginBottom:4}}>{k.toUpperCase()}</div>
            <div style={{fontSize:13,fontWeight:600}}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{overflowX:'auto',borderRadius:8,border:'1px solid var(--border)',marginBottom:14}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead>
            <tr style={{background:'var(--s2)'}}>
              {['Product','SKU','UoM','Qty','Received','Unit Cost','Total'].map(h=>(
                <th key={h} style={{padding:'8px 10px',textAlign:['Qty','Received','Unit Cost','Total'].includes(h)?'right':'left',fontSize:11,fontWeight:700,color:'var(--tm)',borderBottom:'1px solid var(--border)'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(po.items||[]).map((it,i)=>(
              <tr key={i} style={{borderBottom:'1px solid var(--border)',background:i%2===0?'var(--surface)':'var(--s2)'}}>
                <td style={{padding:'8px 10px',fontWeight:600}}>{it.product?.name||'—'}</td>
                <td style={{padding:'8px 10px',fontFamily:'monospace',fontSize:11,color:'var(--primary)'}}>{it.product?.sku||'—'}</td>
                <td style={{padding:'8px 10px',fontSize:12,color:'var(--td)'}}>{it.uom?.abbreviation||it.uom?.name||'—'}</td>
                <td style={{padding:'8px 10px',textAlign:'right'}}>{it.quantity}</td>
                <td style={{padding:'8px 10px',textAlign:'right',color:it.received_qty>=it.quantity?'var(--success)':'var(--td)'}}>{it.received_qty||0}</td>
                <td style={{padding:'8px 10px',textAlign:'right'}}>{fmt(it.unit_cost)}</td>
                <td style={{padding:'8px 10px',textAlign:'right',fontWeight:700}}>{fmt((it.unit_cost||0)*(it.quantity||0))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{background:'var(--s2)'}}>
              <td colSpan={6} style={{padding:'10px',textAlign:'right',fontWeight:700,fontSize:13}}>Total:</td>
              <td style={{padding:'10px',textAlign:'right',fontWeight:800,fontSize:15,color:'var(--primary)'}}>{fmt(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      {po.notes&&<div style={{padding:12,background:'var(--s2)',borderRadius:8,fontSize:13,color:'var(--td)'}}><strong>Notes:</strong> {po.notes}</div>}
      <div className="modal-footer"><button className="btn btn-ghost" onClick={onClose}>Close</button></div>
    </Modal>
  );
}

// ── Main Page ────────────────────────────────────────────────────
export default function PurchaseOrdersPage(){
  const{isOwner,myBranchId,hasPermission}=useAuth();
  const canSeeCost=hasPermission('cost_price.view');
  const canCreate=hasPermission('purchase_orders.create');
  const canEdit=hasPermission('purchase_orders.edit');
  const canReceive=hasPermission('purchase_orders.receive');
  const canDelete=hasPermission('purchase_orders.delete');
  const[data,setData]=useState([]);
  const[ld,setLd]=useState(true);
  const[search,setSearch]=useState('');
  const[statusFilter,setStatusFilter]=useState('All');
  const[vendors,setVendors]=useState([]);
  const[products,setProducts]=useState([]);
  const[uoms,setUoms]=useState([]);
  const[branches,setBranches]=useState([]);
  const[modal,setModal]=useState(null); // null|'new'|'edit'|'view'|'receive'|'payment'
  const[selPO,setSelPO]=useState(null);
  const[payAmount,setPayAmount]=useState('');
  const[delId,setDelId]=useState(null);
  const[saving,setSaving]=useState(false);

  const blankForm={
    vendor_id:'',branch_id:myBranchId||'',order_date:new Date().toISOString().slice(0,10),
    expected_date:'',payment_terms:'',notes:'',status:'draft',
    items:[{product_id:'',uom_id:'',qty:1,unit_cost:0}],
  };
  const[form,setForm]=useState(blankForm);

  // load meta
  useEffect(()=>{
    Promise.all([
      vendorAPI.getAll({limit:200}),
      productAPI.getAll({limit:500}),
      productAPI.getUoms(),
      branchAPI.getAll({limit:100}),
    ]).then(([v,p,u,b])=>{
      setVendors(v.data.data||v.data);
      setProducts(p.data.data||p.data);
      setUoms(u.data.data||u.data);
      setBranches(b.data.data||b.data);
    }).catch(()=>{});
  },[]);

  const load=useCallback(async()=>{
    try{
      const params={search,limit:100};
      if(statusFilter!=='All') params.status=statusFilter;
      // Use resourceController generic endpoint fallback
      const r=await poAPI.getAll(params);
      setData(r.data.data||r.data||[]);
    }catch{
      // If endpoint doesn't exist yet, show empty with message
      setData([]);
    }finally{setLd(false);}
  },[search,statusFilter]);

  useEffect(()=>{const t=setTimeout(load,300);return()=>clearTimeout(t);},[load]);

  const F=(k,v)=>setForm(f=>({...f,[k]:v}));

  const updateItem=(idx,key,val)=>setForm(f=>({
    ...f,items:f.items.map((it,i)=>i===idx?{...it,[key]:val}:it)
  }));
  const removeItem=idx=>setForm(f=>({...f,items:f.items.filter((_,i)=>i!==idx)}));
  const addItem=()=>setForm(f=>({...f,items:[...f.items,{product_id:'',uom_id:'',qty:1,unit_cost:0}]}));

  // Auto-fill cost from vendor's product default
  const handleVendorChange=vid=>{
    F('vendor_id',vid);
    // pre-fill items if vendor has default products — optional enhancement
  };

  const grandTotal=form.items.reduce((s,it)=>s+(parseFloat(it.qty)||0)*(parseFloat(it.unit_cost)||0),0);

  const save=async()=>{
    if(!form.vendor_id) return toast.error('Vendor required');
    if(!form.branch_id) return toast.error('Branch required');
    if(!form.order_date) return toast.error('Order date required');
    const validItems=form.items.filter(it=>it.product_id&&parseFloat(it.qty)>0);
    if(!validItems.length) return toast.error('Add at least one product line');

    setSaving(true);
    try{
      const payload={
        ...form,
        vendor_id:parseInt(form.vendor_id),
        branch_id:parseInt(form.branch_id),
        items:validItems.map(it=>({
          product_id:parseInt(it.product_id),
          uom_id:parseInt(it.uom_id)||null,
          quantity:parseFloat(it.qty)||1,
          unit_cost:parseFloat(it.unit_cost)||0,
        })),
      };
      if(modal==='new') await poAPI.create(payload);
      else await poAPI.update(form.id,payload);
      toast.success(modal==='new'?'Purchase Order created':'Updated');
      setModal(null);
      load();
    }catch(e){toast.error(e.response?.data?.error||'Failed to save');}
    finally{setSaving(false);}
  };

  const setStatus=async(po,status)=>{
    try{await poAPI.updateStatus(po.id,status);toast.success(`Status → ${STATUS_LABEL[status]}`);load();}
    catch(e){toast.error(e.response?.data?.error||'Failed');}
  };

  const updatePayment=async()=>{
    try{
      await poAPI.updatePayment(selPO.id,{paid_amount:parseFloat(payAmount)||0});
      toast.success('Payment updated');setModal(null);load();
    }catch(e){toast.error(e.response?.data?.error||'Failed');}
  };

  const openPayment=async(po)=>{
    try{const r=await poAPI.getById(po.id);setSelPO(r.data);setPayAmount(r.data.paid_amount||'');setModal('payment');}
    catch{toast.error('Failed to load PO');}
  };

  const del=async()=>{
    try{await poAPI.delete(delId);toast.success('Deleted');setDelId(null);load();}
    catch(e){toast.error(e.response?.data?.error||'Cannot delete');}
  };

  const openEdit=async(po)=>{
    try{
      const r=await poAPI.getById(po.id);
      const p=r.data;
      setForm({
        ...p,
        vendor_id:String(p.vendor_id||''),
        branch_id:String(p.branch_id||''),
        order_date:p.order_date?p.order_date.slice(0,10):'',
        expected_date:p.expected_date?p.expected_date.slice(0,10):'',
        items:(p.items||[]).map(it=>({
          product_id:String(it.product_id||''),
          uom_id:String(it.uom_id||''),
          qty:it.quantity,unit_cost:it.unit_cost,
        })),
      });
      setModal('edit');
    }catch{toast.error('Failed to load PO');}
  };

  const openView=async(po)=>{
    try{const r=await poAPI.getById(po.id);setSelPO(r.data);setModal('view');}
    catch{toast.error('Failed to load PO');}
  };

  const openReceive=async(po)=>{
    try{const r=await poAPI.getById(po.id);setSelPO(r.data);setModal('receive');}
    catch{toast.error('Failed to load PO');}
  };

  const vendorOpts=[{value:'',label:'— Select Vendor —'},...vendors.map(v=>({value:String(v.id),label:v.name}))];
  const branchOpts=[{value:'',label:'— Select Branch —'},...branches.map(b=>({value:String(b.id),label:b.name}))];
  const uomOpts=[{value:'',label:'UoM'},...uoms.map(u=>({value:String(u.id),label:`${u.name} (${u.abbreviation})`}))];
  const filterOpts=['All',...STATUSES];

  return(
    <div>
      <PageHeader
        title="Purchase Orders"
        subtitle={`${data.length} orders`}
        action={
          canCreate && (
            <button className="btn btn-primary" onClick={()=>{setForm({...blankForm,branch_id:myBranchId||''});setModal('new');}}>
              <Plus size={16}/> New PO
            </button>
          )
        }
      />

      <div className="filter-bar">
        <div className="flex-1" style={{minWidth:200}}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search PO number, vendor..."/>
        </div>
        <FilterButtons options={filterOpts} active={statusFilter} onChange={setStatusFilter}/>
      </div>

      <DataTable loading={ld} data={data} columns={[
        {key:'po_number',label:'PO #',render:r=><span className="text-accent" style={{fontFamily:'monospace',fontSize:12}}>{r.po_number||`PO-${r.id}`}</span>},
        {key:'vendor',label:'Vendor',render:r=><div><div className="font-bold">{r.vendor?.name||'—'}</div><div style={{fontSize:11,color:'var(--td)'}}>{r.vendor?.contact_person||''}</div></div>},
        {key:'branch',label:'Branch',render:r=>r.branch?.name||'—'},
        {key:'order_date',label:'Order Date',render:r=>fmtDate(r.order_date)},
        {key:'expected_date',label:'Expected',render:r=>r.expected_date?fmtDate(r.expected_date):<span className="text-dim">—</span>},
        {key:'items',label:'Items',render:r=><Badge variant="info">{(r.items||[]).length}</Badge>},
        ...(canSeeCost?[{key:'total',label:'Total',render:r=>{
          const t=(r.items||[]).reduce((s,i)=>s+(parseFloat(i.unit_cost)||0)*(i.quantity||0),0);
          return<span className="font-bold">{fmt(t)}</span>;
        }}]:[]),
        {key:'status',label:'Status',render:r=><Badge variant={STATUS_COLOR[r.status]||'info'}>{STATUS_LABEL[r.status]||r.status}</Badge>},
        {key:'payment_status',label:'Payment',render:r=><Badge variant={PAY_COLOR[r.payment_status||'unpaid']}>{PAY_LABEL[r.payment_status||'unpaid']}</Badge>},
      ]} actions={r=>(
        <div className="flex gap-2">
          <button className="btn btn-ghost btn-sm" onClick={()=>openView(r)} title="View"><Eye size={14}/></button>
          {canEdit&&['draft','sent'].includes(r.status)&&(
            <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(r)} title="Edit"><Edit size={14}/></button>
          )}
          {canReceive&&['confirmed','partial'].includes(r.status)&&(
            <button className="btn btn-primary btn-sm" onClick={()=>openReceive(r)} title="Receive Stock" style={{gap:4}}>
              <Package size={13}/> Receive
            </button>
          )}
          {canEdit&&['confirmed','partial','received'].includes(r.status)&&(r.payment_status||'unpaid')!=='paid'&&(
            <button className="btn btn-ghost btn-sm" onClick={()=>openPayment(r)} title="Record Payment"
              style={{color:'var(--success)',gap:4}}>
              <DollarSign size={13}/> Pay
            </button>
          )}
          {canEdit&&r.status==='draft'&&(
            <button className="btn btn-ghost btn-sm" onClick={()=>setStatus(r,'sent')} title="Mark Sent" style={{fontSize:11}}>
              <Truck size={13}/> Send
            </button>
          )}
          {canEdit&&r.status==='sent'&&(
            <button className="btn btn-ghost btn-sm" onClick={()=>setStatus(r,'confirmed')} title="Confirm" style={{fontSize:11}}>
              <CheckCircle size={13}/> Confirm
            </button>
          )}
          {canEdit&&!['received','cancelled'].includes(r.status)&&(
            <button className="btn btn-ghost btn-sm" onClick={()=>setStatus(r,'cancelled')} title="Cancel" style={{color:'var(--danger)'}}>
              <XCircle size={13}/>
            </button>
          )}
          {canDelete&&r.status==='draft'&&(
            <button className="btn btn-danger btn-sm" onClick={()=>setDelId(r.id)}><Trash2 size={14}/></button>
          )}
        </div>
      )}/>

      {/* ── CREATE / EDIT MODAL ── */}
      <Modal open={modal==='new'||modal==='edit'} onClose={()=>setModal(null)}
        title={modal==='new'?'New Purchase Order':'Edit Purchase Order'} width={800}>
        <div className="form-grid" style={{marginBottom:14}}>
          <FormInput label="Vendor *" value={String(form.vendor_id||'')} onChange={handleVendorChange} options={vendorOpts}/>
          <FormInput label="Branch *" value={String(form.branch_id||'')} onChange={v=>F('branch_id',v)} options={branchOpts}/>
          <FormInput label="Order Date *" value={form.order_date||''} onChange={v=>F('order_date',v)} type="date"/>
          <FormInput label="Expected Delivery" value={form.expected_date||''} onChange={v=>F('expected_date',v)} type="date"/>
          <FormInput label="Payment Terms" value={form.payment_terms||''} onChange={v=>F('payment_terms',v)} placeholder="e.g. Net 30"/>
          <FormInput label="Status" value={form.status||'draft'} onChange={v=>F('status',v)}
            options={STATUSES.map(s=>({value:s,label:STATUS_LABEL[s]}))}/>
        </div>

        {/* Line items */}
        <div style={{marginBottom:14}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:'var(--tm)',letterSpacing:.4}}>ORDER LINES</div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={addItem} style={{fontSize:11}}>
              <Plus size={12}/> Add Line
            </button>
          </div>
          <div style={{overflowX:'auto',borderRadius:8,border:'1px solid var(--border)'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{background:'var(--s2)'}}>
                  {['Product','UoM (from product)','Qty',...(canSeeCost?['Unit Cost ($)','Total']:[]),''].map(h=>(
                    <th key={h} style={{padding:'8px 10px',textAlign:['Qty','Unit Cost ($)','Total'].includes(h)?'right':'left',fontSize:11,fontWeight:700,color:'var(--tm)',borderBottom:'1px solid var(--border)'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {form.items.map((row,i)=>(
                  <LineRow key={i} row={row} idx={i} products={products}
                    onChange={updateItem} onRemove={removeItem} canSeeCost={canSeeCost}/>
                ))}
                {form.items.length===0&&(
                  <tr><td colSpan={canSeeCost?6:4} style={{textAlign:'center',padding:20,color:'var(--td)',fontSize:13}}>No lines — click "+ Add Line"</td></tr>
                )}
              </tbody>
              {canSeeCost&&form.items.length>0&&(
                <tfoot>
                  <tr style={{background:'var(--s2)'}}>
                    <td colSpan={4} style={{padding:'10px',textAlign:'right',fontWeight:700,fontSize:13}}>Grand Total:</td>
                    <td style={{padding:'10px',textAlign:'right',fontWeight:800,fontSize:15,color:'var(--primary)'}}>{fmt(grandTotal)}</td>
                    <td/>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        <FormInput label="Notes" value={form.notes||''} onChange={v=>F('notes',v)} type="textarea"/>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving?<><RefreshCw size={14} style={{animation:'spin 1s linear infinite'}}/> Saving…</>:'Save PO'}
          </button>
        </div>
      </Modal>

      {/* ── DETAIL VIEW ── */}
      <DetailModal open={modal==='view'} po={selPO} onClose={()=>setModal(null)}/>

      {/* ── RECEIVE STOCK ── */}
      <ReceiveModal
        open={modal==='receive'}
        po={selPO}
        onClose={()=>setModal(null)}
        onDone={load}
      />

      {/* ── DELETE ── */}
      <Modal open={!!delId} onClose={()=>setDelId(null)} title="Delete Purchase Order" width={400}>
        <p className="text-muted" style={{marginBottom:18}}>Delete this draft PO permanently?</p>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={()=>setDelId(null)}>Cancel</button>
          <button className="btn btn-danger" onClick={del}>Delete</button>
        </div>
      </Modal>

      {/* ── PAYMENT MODAL ── */}
      <Modal open={modal==='payment'} onClose={()=>setModal(null)} title="Record Payment" width={440}>
        {selPO&&(()=>{
          const total=(selPO.items||[]).reduce((s,i)=>s+(parseFloat(i.unit_cost)||0)*(i.quantity||0),0);
          const paid=parseFloat(payAmount)||0;
          const balance=total-paid;
          return(
            <div>
              {/* PO summary */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:16}}>
                {[
                  ['PO Number',selPO.po_number],
                  ['Vendor',selPO.vendor?.name||'—'],
                  ['Payment Terms',selPO.payment_terms||'—'],
                  ['Current Status',<Badge key="ps" variant={PAY_COLOR[selPO.payment_status||'unpaid']}>{PAY_LABEL[selPO.payment_status||'unpaid']}</Badge>],
                ].map(([k,v])=>(
                  <div key={k} style={{background:'var(--s2)',borderRadius:8,padding:'10px 12px'}}>
                    <div style={{fontSize:10,color:'var(--td)',fontWeight:700,letterSpacing:.4,marginBottom:3}}>{k.toUpperCase()}</div>
                    <div style={{fontSize:13,fontWeight:600}}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Amount bar */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:18}}>
                <div style={{textAlign:'center',padding:'12px 8px',background:'var(--s2)',borderRadius:8,border:'1px solid var(--border)'}}>
                  <div style={{fontSize:11,color:'var(--td)',fontWeight:700,marginBottom:4}}>TOTAL VALUE</div>
                  <div style={{fontSize:18,fontWeight:800,color:'var(--text)'}}>{fmt(total)}</div>
                </div>
                <div style={{textAlign:'center',padding:'12px 8px',background:'rgba(0,212,170,0.08)',borderRadius:8,border:'1px solid rgba(0,212,170,0.3)'}}>
                  <div style={{fontSize:11,color:'var(--td)',fontWeight:700,marginBottom:4}}>PAYING NOW</div>
                  <div style={{fontSize:18,fontWeight:800,color:'var(--success)'}}>{fmt(paid)}</div>
                </div>
                <div style={{textAlign:'center',padding:'12px 8px',background:balance>0?'rgba(255,68,68,0.08)':'rgba(0,212,170,0.08)',borderRadius:8,border:`1px solid ${balance>0?'rgba(255,68,68,0.3)':'rgba(0,212,170,0.3)'}`}}>
                  <div style={{fontSize:11,color:'var(--td)',fontWeight:700,marginBottom:4}}>BALANCE DUE</div>
                  <div style={{fontSize:18,fontWeight:800,color:balance>0?'var(--danger)':'var(--success)'}}>{fmt(balance)}</div>
                </div>
              </div>

              <div style={{marginBottom:16}}>
                <label style={{fontSize:12,fontWeight:700,color:'var(--tm)',display:'block',marginBottom:6}}>TOTAL PAID AMOUNT ($)</label>
                <input type="number" min={0} step={0.01} className="form-input"
                  value={payAmount}
                  onChange={e=>setPayAmount(e.target.value)}
                  placeholder="Enter total amount paid..."
                  style={{fontSize:16,fontWeight:700,textAlign:'right'}}/>
                <div style={{marginTop:8,display:'flex',gap:8,flexWrap:'wrap'}}>
                  {[['Pay Half',+(total/2).toFixed(2)],['Pay Full',+total.toFixed(2)]].map(([label,amt])=>(
                    <button key={label} className="btn btn-ghost btn-sm" onClick={()=>setPayAmount(String(amt))}
                      style={{fontSize:11}}>{label} ({fmt(amt)})</button>
                  ))}
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
                <button className="btn btn-primary" onClick={updatePayment} style={{gap:6}}>
                  <DollarSign size={14}/> Save Payment
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
